import { getProjectStatus } from '../../../utils/dataNormalization';

/**
 * Filter jobs based on search term and HideFromList criteria
 * @param {Array} jobs - Array of job objects to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered jobs array
 */
export const filterJobsGlobally = (jobs, searchTerm) => {
  if (!Array.isArray(jobs)) {
    return [];
  }

  let filteredJobs = jobs;

  // First, filter out jobs where HideFromList is true or "true"
  filteredJobs = filteredJobs.filter(job => {
    const hideFromList = job.HideFromList;
    // Filter out if HideFromList is true (boolean) or "true" (string)
    return hideFromList !== true && hideFromList !== "true";
  });

  // Then apply search filter if there's a search term
  if (searchTerm && searchTerm.trim()) {
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    
    filteredJobs = filteredJobs.filter(job => {
      // Search in JobNumber, ProjectName, and ModelNumber fields
      const jobNumber = (job.JobNumber || "").toLowerCase();
      const projectName = (job.ProjectName || "").toLowerCase();
      const modelNumber = (job.ModelNumber || "").toLowerCase();
      
      return jobNumber.includes(lowerSearchTerm) ||
             projectName.includes(lowerSearchTerm) ||
             modelNumber.includes(lowerSearchTerm);
    });
  }

  return filteredJobs;
};

/**
 * Filter jobs for specific component types (shipped, canceled, on hold)
 * @param {Array} jobs - Array of job objects to filter
 * @param {string} searchTerm - Search term to filter by
 * @param {string} statusFilter - Status to filter by ('Shipped', 'canceled', 'On Hold')
 * @returns {Array} - Filtered jobs array
 */
export const filterJobsByStatus = (jobs, searchTerm, statusFilter) => {
  if (!Array.isArray(jobs)) {
    return [];
  }

  // First apply global filtering (search + HideFromList)
  let filteredJobs = filterJobsGlobally(jobs, searchTerm);  // Then filter by status
  if (statusFilter) {
    if (statusFilter === 'Shipped') {
      // For shipped jobs, check both ProjectStatus and JobStatus
      filteredJobs = filteredJobs.filter(job => 
        getProjectStatus(job) === statusFilter
      );
    } else if (statusFilter === 'On Hold') {
      // For on hold jobs, check for multiple status variations
      filteredJobs = filteredJobs.filter(job => {
        const jobStatus = job.JobStatus?.trim();
        const hasNoShipDate = job.ActualShipDate === null || job.ActualShipDate === undefined || job.ActualShipDate === "";
        return (
          (jobStatus === "On Hold" || jobStatus === "Hold" || jobStatus === "Hold/ Ready to Ship") && 
          hasNoShipDate
        );
      });
    } else {
      // For other statuses, use the original logic
      filteredJobs = filteredJobs.filter(job => 
        getProjectStatus(job) === statusFilter
      );
    }
  }

  return filteredJobs;
};

/**
 * Filter jobs for enhanced schedule table (Full Release jobs only)
 * @param {Array} jobs - Array of job objects to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered jobs array
 */
export const filterJobsForSchedule = (jobs, searchTerm) => {
  if (!Array.isArray(jobs)) {
    return [];
  }
  // First apply global filtering (search + HideFromList)
  let filteredJobs = filterJobsGlobally(jobs, searchTerm);

  // Then filter by allowed JobStatus values for the main schedule
  const allowedStatuses = [
    "Full Release",
    "Engineering Only",
    "Long Lead Only",
    "Release with Qualified Exceptions",
    "Release Pending CSO",
    "Release with CSO Pending",
    "Release w/ CSO Pending"
  ];
  
  filteredJobs = filteredJobs.filter(job => {
    const jobStatus = getProjectStatus(job);
    return allowedStatuses.includes(jobStatus);
  });

  return filteredJobs;
};

/**
 * Filter jobs for engineering schedule with optional past due items
 * @param {Array} jobs - Array of job objects to filter
 * @param {string} searchTerm - Search term to filter by
 * @param {boolean} includePastDue - Whether to include items outside date range and past due
 * @param {Object} workWeekColumns - Work week columns for date filtering
 * @param {number} startWeekIndex - Start week index for filtering
 * @param {number} endWeekIndex - End week index for filtering
 * @returns {Array} - Filtered jobs array
 */
export const filterJobsForEngineering = (
  jobs, 
  searchTerm, 
  includePastDue = true, 
  workWeekColumns = [], 
  startWeekIndex = 0, 
  endWeekIndex = 0
) => {
  if (!Array.isArray(jobs)) {
    return [];
  }

  // First apply global filtering (search + HideFromList)
  let filteredJobs = filterJobsGlobally(jobs, searchTerm);

  // Filter by allowed JobStatus values for engineering
  const allowedStatuses = [
    "Full Release",
    "Engineering Only", 
    "Long Lead Only",
    "Release with Qualified Exceptions",
    "Release Pending CSO",
    "Release with CSO Pending",
    "Release w/ CSO Pending"
  ];
  
  filteredJobs = filteredJobs.filter(job => {
    const jobStatus = getProjectStatus(job);
    return allowedStatuses.includes(jobStatus);
  });

  // If not including past due items, filter by date range
  if (!includePastDue && workWeekColumns && workWeekColumns.length > 0) {
    const startWeek = workWeekColumns[startWeekIndex];
    const endWeek = workWeekColumns[endWeekIndex];
    
    if (startWeek && endWeek) {
      const moment = require('moment');
      const firstDay = moment(startWeek.startDate);
      const lastDay = moment(endWeek.endDate);
      
      filteredJobs = filteredJobs.filter(job => {
        if (!job.EngineeringDue) return false;
        const jobDueDate = moment(job.EngineeringDue);
        return jobDueDate.isBetween(firstDay, lastDay, 'day', '[]'); // Inclusive range
      });
    }
  }

  return filteredJobs;
};
